import { useCallback, useEffect, useRef, useState } from 'react';
import type { Answers } from '../../lib/types';

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Debounced autosave. Only one request is in flight at a time; if answers change while saving,
 * another save runs afterwards with the latest answers.
 */
export function useAutosave(
  answers: Answers,
  save: ((answers: Answers) => Promise<unknown>) | undefined,
  delayMs = 1200,
) {
  const [state, setState] = useState<SaveState>('idle');
  const latest = useRef(answers);
  const lastSaved = useRef(JSON.stringify(answers));
  const inFlight = useRef<Promise<void> | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  latest.current = answers;

  const flush = useCallback(async (): Promise<void> => {
    clearTimeout(timer.current);
    if (!save) return;
    if (inFlight.current) await inFlight.current;
    const snapshot = JSON.stringify(latest.current);
    if (snapshot === lastSaved.current) return;

    setState('saving');
    inFlight.current = save(latest.current)
      .then(() => {
        lastSaved.current = snapshot;
        setState('saved');
      })
      .catch(() => setState('error'))
      .finally(() => {
        inFlight.current = null;
      });
    await inFlight.current;
  }, [save]);

  useEffect(() => {
    if (!save || JSON.stringify(answers) === lastSaved.current) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => void flush(), delayMs);
    return () => clearTimeout(timer.current);
  }, [answers, save, flush, delayMs]);

  // Warn before closing the tab while there are unsaved changes.
  useEffect(() => {
    if (!save) return;
    const handler = (e: BeforeUnloadEvent) => {
      if (JSON.stringify(latest.current) !== lastSaved.current) e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [save]);

  /** Marks the current answers as saved (e.g. after a successful submit). */
  const markSaved = useCallback(() => {
    clearTimeout(timer.current);
    lastSaved.current = JSON.stringify(latest.current);
  }, []);

  return { state, flush, markSaved };
}
